import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';

type ElementStatus = 'pending' | 'extracted' | 'validated' | 'error';
type Substep = 'extract' | 'verify' | 'approved' | 'rerun';

export interface TraceEntry {
  request: string;
  response: string;
}

export interface ElementPayload {
  id: string;
  title?: string;
  description?: string;
  code?: string;
  version?: string;
  [key: string]: unknown;
}

export interface ElementData {
  name: string;
  status: ElementStatus;
  notes?: string;
  payload: ElementPayload;
  sourceSpans: Array<{ page: number; y: number; height: number; label?: string }>;
}

@Component({
  selector: 'app-usdm',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './usdm.component.html',
  styleUrl: './usdm.component.css'
})
export class UsdmComponent {
  readonly steps: string[] = [
    'Study Creation',
    'Data Extraction',
    'USDM Generation',
    'USDM Validation'
  ];

  readonly catalog: string[] = [
    'Arms',
    'Population',
    'Criteria',
    'Objectives',
    'Endpoints',
    'Interventions',
    'Visits',
    'Activities',
    'Schedule',
    'Study Design'
  ];

  readonly pdfPreviewPages = [1, 2, 3];
  readonly pdfFocusPages = [1, 2];

  step = 1;
  elements: Record<string, ElementData> = this.createInitialElements();
  active: ElementData | null = null;
  generationTab: 'viewer' | 'raw' = 'viewer';
  jsonText = '';
  llmTraces: Record<string, TraceEntry[]> = {};
  panelRerunComment = '';
  substeps: Record<string, Substep | null> = this.createInitialSubsteps();
  usdmJson = `{
  "usdm": "sample",
  "elements": 10
}`;

  get extractedCount(): number {
    return Object.values(this.elements).filter(e => e.status !== 'pending').length;
  }

  get validatedCount(): number {
    return Object.values(this.elements).filter(e => e.status === 'validated').length;
  }

  setStep(step: number): void {
    this.step = step;
  }

  markExtracted(name: string): void {
    this.updateElement(name, { status: 'extracted' });
    this.updateSubstep(name, 'extract');
    this.appendTrace(name, {
      request: `Extract ${name} from protocol (prompt)`,
      response: `{ "sample": "${name} extracted" }`
    });
  }

  markVerified(name: string): void {
    this.updateElement(name, { status: 'extracted' });
    this.updateSubstep(name, 'verify');
  }

  approve(name: string): void {
    this.updateElement(name, {
      status: 'validated',
      notes: undefined
    });
    this.updateSubstep(name, 'approved');
  }

  rerun(name: string, comment: string | null = null): void {
    this.updateElement(name, {
      status: 'pending',
      notes: comment ?? 'Queued for rerun'
    });
    this.updateSubstep(name, 'rerun');
  }

  runAllForElement(name: string): void {
    this.markExtracted(name);
    this.markVerified(name);
    this.approve(name);
  }

  runAllElements(): void {
    this.catalog.forEach(name => this.runAllForElement(name));
  }

  openPanel(name: string): void {
    const element = this.elements[name];
    if (!element) return;

    this.active = this.deepClone(element);
    this.jsonText = JSON.stringify(element.payload, null, 2);
    this.panelRerunComment = '';
  }

  closePanel(): void {
    this.active = null;
    this.panelRerunComment = '';
  }

  saveForm(): void {
    if (!this.active) return;
    this.updateElement(this.active.name, this.deepClone(this.active));
  }

  saveJson(): void {
    if (!this.active) return;
    try {
      const parsed = JSON.parse(this.jsonText) as ElementPayload;
      const updated: ElementData = {
        ...this.active,
        payload: parsed
      };
      this.active = updated;
      this.updateElement(updated.name, this.deepClone(updated));
    } catch (error) {
      window.alert('Invalid JSON');
    }
  }

  resetJsonText(): void {
    if (!this.active) return;
    this.jsonText = JSON.stringify(this.active.payload, null, 2);
  }

  approveFromPanel(): void {
    if (!this.active) return;
    this.saveForm();
    this.approve(this.active.name);
    this.closePanel();
  }

  rerunFromPanel(): void {
    if (!this.active) return;
    const comment = this.panelRerunComment.trim();
    if (!this.canSubmitRerun) return;
    this.saveForm();
    this.rerun(this.active.name, comment);
    this.panelRerunComment = '';
    this.closePanel();
  }

  get canSubmitRerun(): boolean {
    return this.panelRerunComment.trim().length > 0;
  }

  getProgress(name: string): number {
    const status = this.substeps[name];
    switch (status) {
      case 'extract':
        return 33;
      case 'verify':
        return 66;
      case 'approved':
        return 100;
      default:
        return 0;
    }
  }

  getTracesForActive(): TraceEntry[] {
    if (!this.active) return [];
    return this.llmTraces[this.active.name] ?? [
      {
        request: 'LLM call prompt sample',
        response: 'Model output sample'
      }
    ];
  }

  private createInitialElements(): Record<string, ElementData> {
    const entries = this.catalog.map(name => [
      name,
      {
        name,
        status: 'pending' as ElementStatus,
        payload: {
          id: name.toLowerCase().replace(/\s+/g, '_'),
          title: `${name} draft`,
          description: 'Auto-extracted sample text for demonstration.'
        },
        sourceSpans: [
          { page: 1, y: 120, height: 30, label: 'match' },
          { page: 2, y: 300, height: 24, label: 'match' }
        ]
      }
    ]);

    return Object.fromEntries(entries) as Record<string, ElementData>;
  }

  private createInitialSubsteps(): Record<string, Substep | null> {
    return Object.fromEntries(this.catalog.map(name => [name, null])) as Record<string, Substep | null>;
  }

  private updateElement(name: string, partial: Partial<ElementData>): void {
    const current = this.elements[name];
    if (!current) return;
    const updated = {
      ...this.elements,
      [name]: {
        ...current,
        ...partial
      }
    };
    this.elements = updated;
  }

  private updateSubstep(name: string, status: Substep): void {
    const updated = { ...this.substeps, [name]: status };
    this.substeps = updated;
  }

  private appendTrace(name: string, trace: TraceEntry): void {
    const current = this.llmTraces[name] ?? [];
    this.llmTraces = {
      ...this.llmTraces,
      [name]: [...current, trace]
    };
  }

  private deepClone<T>(value: T): T {
    const globalRef = globalThis as typeof globalThis & { structuredClone?: <K>(data: K) => K };
    if (typeof globalRef.structuredClone === 'function') {
      return globalRef.structuredClone(value);
    }
    return JSON.parse(JSON.stringify(value)) as T;
  }
}
